import { motion } from 'framer-motion';

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  radius?: string;
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  animation?: 'pulse' | 'wave' | 'none';
}

export function Skeleton({
  width = '100%',
  height = '20px',
  radius,
  className = '',
  variant = 'rectangular',
  animation = 'pulse',
}: SkeletonProps) {
  const getRadius = () => {
    if (radius) return radius;
    if (variant === 'circular') return '50%';
    if (variant === 'text') return '4px';
    return '8px';
  };

  const baseStyles: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    borderRadius: getRadius(),
    backgroundColor: 'hsl(var(--border))',
    position: 'relative',
    overflow: 'hidden',
  };

  if (animation === 'none') {
    return <div style={baseStyles} className={className} />;
  }

  if (animation === 'wave') {
    return (
      <div style={baseStyles} className={className}>
        <motion.div
          style={{
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, hsl(var(--muted)), transparent)',
          }}
          animate={{
            left: '200%',
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>
    );
  }

  // Default pulse animation
  return (
    <motion.div
      style={baseStyles}
      className={className}
      animate={{
        opacity: [1, 0.5, 1],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  );
}

// Composite skeleton components
export function SkeletonText({ lines = 3, gap = 8 }: { lines?: number; gap?: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: `${gap}px` }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height={16}
          width={i === lines - 1 ? '60%' : '100%'}
          variant="text"
        />
      ))}
    </div>
  );
}

export function SkeletonCard() {
  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="mb-4">
        <Skeleton height={24} width="40%" />
      </div>
      <SkeletonText lines={2} />
      <div className="mt-4 flex gap-3">
        <Skeleton height={32} width={80} radius="8px" />
        <Skeleton height={32} width={80} radius="8px" />
      </div>
    </div>
  );
}
